// src/context/AuthContext.tsx
//
// iSentinel360 v0.4.9.2.3c17 — consolidated AuthContext
//
// c17 = c16 minus the /api/auth/me pre-flight on mount.
//
// Why: c16 (and c9–c15 before it) awaited validateStoredToken(storedJwt)
// before calling Android.setAuth on mount. That opened a race window:
//   1. mount effect starts validateStoredToken(OLD_JWT)
//   2. validation resolves TRUE before the user finishes login
//   3. we call setAuth(OLD_JWT) — APK opens a WS with the OLD JWT
//   4. login POST completes, server replaces the session,
//      the OLD WS gets kicked
//   5. kick handler fires → hard redirect → "logged out"
// c16's race guard only protected against validation resolving
// AFTER login, not BEFORE. We can't reliably win this race, so c17
// re-arms immediately (like c12 did) and lets the server kick a dead
// JWT if one exists. The kick handler hard-redirects (no black screen),
// so a dead JWT produces a clean logout instead of a broken modal.
//
// What's in c17 vs c12:
//   1. Helper: clearAllClientSessionState(reason)
//   2. Helper: clearAndroidBridge()
//   3. updateActiveStoreId: no-op guard via lastSetAuthStoreId ref
//   4. applyLoginResponse:
//        - pre-login clearAllClientSessionState()
//        - role-based clearing of civilRespond_actAsOrgId / actAsOrgs
//          for non-admin users (security fix)
//        - multi-store users defer setAuth until they pick a store
//   5. Kick handler: hard redirect to /login (no modal — the modal
//      didn't render in Android System WebView)
//   6. logout: ref reset + helper-based cleanup
// =============================================================

import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { createCredential, getCredential } from '../lib/webauthn';

const LS_ISENTIALERT_LINKED = 'isentialertLinked';

interface User {
  id: string;
  email: string;
  // v0.4.9.2.4c5: 'tadmin' added — TAdmin (Technical Admin) is an
  // iSentinel360 employee role parallel to SAdmin. Without it here, every
  // `user.role === 'tadmin'` comparison in the app raises TS2367.
  role: 'cadmin' | 'admin' | 'guard' | 'superadmin' | 'sadmin' | 'agent' | 'agentAdmin' | 'tadmin';
  name: string;
  storeId?: string;
  clientId?: string | null;
  trainingModeEnabled?: boolean;
  is_primary_sadmin?: boolean;
  user_type?: 'commercial' | 'civil_respondent';
  civil_respondent_id?: string;
  civil_respondent_type?: 'law_enforcement' | 'emergency_services';
  apkInstalled?: boolean;
  adminEmail?: string | null;
  adminCcEmail?: string | null;
  stores?: {
    store_id: string;
    name: string;
    edge_server_ip?: string;
    wifi_ssid?: string;
  }[];
  trainingEnabled?: boolean;
}

export interface PasskeyInfo {
  credential_id: string;
  nickname: string | null;
  aaguid: string | null;
  created_at: string;
  last_used_at: string | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  activeStoreId: string | null;
  setActiveStoreId: (id: string | null) => void;
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  loginWithPasskey: () => Promise<{ ok: boolean; error?: string }>;
  registerPasskey: (nickname?: string) => Promise<{ ok: boolean; error?: string }>;
  listPasskeys: () => Promise<PasskeyInfo[]>;
  revokePasskey: (credentialId: string) => Promise<boolean>;
  logout: () => Promise<void>;
  // kept for backward-compat; always null now — kick handler hard-redirects
  kickedReason: string | null;
  dismissKickedModal: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = 'https://api.isentinel360.com';

function apkClientHeader(): Record<string, string> {
  if (typeof window !== 'undefined' && (window as any).Android) {
    return { 'X-iSentinel-Client': 'apk' };
  }
  return {};
}

// ---------- AuthProvider ----------
export function AuthProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeStoreId, setActiveStoreId] = useState<string | null>(
    localStorage.getItem('activeStoreId')
  );

  // v0.4.9.2.3c17: tracks the last storeId we pushed to the APK via
  // Android.setAuth. Used by updateActiveStoreId to no-op on
  // re-renders that re-emit the current store.
  const lastSetAuthStoreId = useRef<string | null | undefined>(undefined);

  // ========== HELPER: clear all client-side session state ==========
  const clearAllClientSessionState = (reason: string) => {
    try {
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('activeStoreId');
      localStorage.removeItem('userStores');
      localStorage.removeItem('civilRespond_actAsOrgId');
      localStorage.removeItem('civilRespond_actAsOrgs');
    } catch {
      // localStorage may be unavailable
    }
    console.log('[AuthContext] cleared client session state, reason=', reason);
  };

  // ========== HELPER: clear ONLY the Android bridge state ==========
  // Calls Android.clearAuth(). Used on kick + logout only — NOT on
  // login. On login we call Android.setAuth() which is atomic
  // (replaces the JWT + storeId in one op). Calling clearAuth() before
  // setAuth() opens a window where the APK has neither, and its
  // reconnect logic can pick a different store during that window.
  const clearAndroidBridge = () => {
    if (typeof (window as any).Android?.clearAuth === 'function') {
      try {
        (window as any).Android.clearAuth();
      } catch {
        // ignore
      }
    }
  };

  // v0.4.1+: when the user picks a store (login-time StoreSelectorModal
  // OR mid-session "Switch Store"), this:
  //   1) Updates React state + localStorage
  //   2) Notifies the native APK via Android.setAuth with the picked
  //      storeId.
  const updateActiveStoreId = (id: string | null) => {
    setActiveStoreId(id);
    if (id) {
      localStorage.setItem('activeStoreId', id);
    } else {
      localStorage.removeItem('activeStoreId');
    }

    // v0.4.9.2.3c17: no-op guard. Same-store re-renders no longer
    // trigger setAuth, which would close and reopen the APK's WS.
    if (lastSetAuthStoreId.current === id) {
      console.log('[AuthContext] updateActiveStoreId: unchanged (' + id + '), skipping setAuth');
      return;
    }
    lastSetAuthStoreId.current = id;

    if (id && typeof (window as any).Android?.setAuth === 'function') {
      const jwt = localStorage.getItem('authToken') || '';
      const userJson = localStorage.getItem('currentUser') || '{}';
      try {
        const user = JSON.parse(userJson);
        (window as any).Android.setAuth(JSON.stringify({
          jwt,
          userId: user.id,
          storeId: id,
          displayName: user.name || user.email,
        }));
      } catch {
        // ignore — best-effort
      }
    }
  };

  // =====================================================================
  // iSentiAlert (D14) — Deep-link token exchange
  // =====================================================================
  const handleAlertToken = async (token: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/alerts/exchange-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!response.ok) {
        clearAllClientSessionState('alert_token_expired');
        setUser(null);
        setActiveStoreId(null);
        window.location.href = '/login?reason=alert-token-expired';
        return;
      }
      const data = await response.json();
      localStorage.setItem('authToken', data.jwt);
      localStorage.setItem('currentUser', JSON.stringify(data.user));

      const storedStores = localStorage.getItem('userStores');
      if (storedStores) {
        try {
          const parsed = JSON.parse(storedStores);
          if (Array.isArray(parsed) && parsed.length > 0) {
            data.user.stores = parsed;
          }
        } catch {
          // ignore
        }
      }

      setUser(data.user);
      if (data.user.storeId) {
        setActiveStoreId(data.user.storeId);
        localStorage.setItem('activeStoreId', data.user.storeId);
        lastSetAuthStoreId.current = data.user.storeId;
        if (typeof (window as any).Android?.setAuth === 'function') {
          (window as any).Android.setAuth(JSON.stringify({
            jwt: data.jwt,
            userId: data.user.id,
            storeId: data.user.storeId,
            displayName: data.user.name || data.user.email,
          }));
        }
      }

      piggybackClaim();

      window.location.href = `/alerts/${data.alert_id}`;
    } catch (err) {
      console.error('[AuthContext] exchange-token network error:', err);
    }
  };

  // =====================================================================
  // iSentiAlert (D15) — Pair-claim piggyback
  // =====================================================================
  const piggybackClaim = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/api/setup/pair-claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.linked) {
        localStorage.setItem(LS_ISENTIALERT_LINKED, '1');
        window.dispatchEvent(
          new CustomEvent('isentialert-linked', {
            detail: { text: 'iSentiAlert linked on this device' },
          })
        );
      } else {
        localStorage.setItem(LS_ISENTIALERT_LINKED, '0');
      }
    } catch (err) {
      console.error('[AuthContext] pair-claim error:', err);
    }
  };

  // =====================================================================
  // Mount effect — v0.4.9.2.3c17
  //
  // Re-arms the APK bridge IMMEDIATELY with the stored JWT (like c12).
  // No /api/auth/me pre-flight. If the stored JWT is dead, the server
  // kicks the WS and the kick handler hard-redirects to /login.
  // =====================================================================
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      if (token) {
        handleAlertToken(token);
        return;
      }

      const storedUser = localStorage.getItem('currentUser');
      const storedStores = localStorage.getItem('userStores');
      const storedActiveStoreId = localStorage.getItem('activeStoreId');
      const storedJwt = localStorage.getItem('authToken');

      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (storedStores) {
            try { parsedUser.stores = JSON.parse(storedStores); } catch { /* ignore */ }
          }
          setUser(parsedUser);
          if (storedActiveStoreId) setActiveStoreId(storedActiveStoreId);
        } catch (e) {
          console.warn('[AuthContext] Corrupt localStorage user, clearing:', e);
          localStorage.removeItem('currentUser');
          localStorage.removeItem('userStores');
        }
      }
      setLoading(false);

      // v0.4.9.2.3c18: For B2G agents whose storedActiveStoreId is
      // null (data.stores is empty for civil respondents), derive
      // a bridge id from civil_respondent_id so we can re-arm on
      // app relaunch. Without this, agents lose the FGS notification
      // every time they close/reopen the APK.
      let mountStoreId = storedActiveStoreId;
      if (!mountStoreId && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          const userType = String(parsedUser?.user_type || '').toLowerCase();
          if (userType === 'civil_respondent') {
            mountStoreId = parsedUser.civil_respondent_id || null;
          }
        } catch { /* ignore corrupt JSON */ }
      }

      // Only re-arm if we have the full triple AND a bridge to talk to.
      if (storedUser && mountStoreId && storedJwt) {
        const tryReArm = (attempts: number) => {
          if (typeof (window as any).Android?.setAuth === 'function') {
            try {
              const user = JSON.parse(storedUser);
              lastSetAuthStoreId.current = mountStoreId;
              console.log('[AuthContext] Mount-time setAuth (attempt ' + attempts + ')');
              (window as any).Android.setAuth(JSON.stringify({
                jwt: storedJwt,
                userId: user.id,
                storeId: mountStoreId,
                displayName: user.name || user.email,
              }));
            } catch (e) {
              console.error('[AuthContext] Mount-time setAuth failed:', e);
            }
          } else if (attempts < 50) {
            setTimeout(() => tryReArm(attempts + 1), 100);
          } else {
            console.warn('[AuthContext] Mount-time setAuth gave up after 50 attempts: window.Android.setAuth never became a function');
          }
        };
        tryReArm(1);
      }
    } catch (e) {
      console.error('[AuthContext] Mount effect failed:', e);
      setLoading(false);
    }
  }, []);

  const getAuthHeader = (): string => {
    const t = localStorage.getItem('authToken');
    return t ? `Bearer ${t}` : '';
  };

  // After a successful login (password OR passkey) the backend returns
  // the same { token, user, stores } shape.
  const applyLoginResponse = (data: { token: string; user: any; stores?: any[] }): void => {
    // v0.4.9.2.3c17: wipe prior session state BEFORE writing the new
    // one. Prevents the APK's KeepAliveService from briefly running
    // with a stale JWT while the new one propagates.
    clearAllClientSessionState('pre_login');

    // v0.4.9.2.3c17 (from c13): role-based actAsOrgId clearing.
    // The civilRespond_actAsOrgId / civilRespond_actAsOrgs keys are
    // admin-only (sadmin / superadmin use them to act as an org they
    // don't belong to). When a non-admin user logs in on the same
    // device — even if a previous SuperAdmin session left the key set
    // — they MUST NOT inherit it, because:
    //   1. CivilRespondFooter reads it and shows
    //      "Admin mode — <wrong-org> (<wrong-country>)" to agents.
    //   2. The BOLO API uses it to filter the agent's view, so a
    //      wrong actAsOrgId sends their BOLO requests to a foreign org.
    const role = String(data?.user?.role || '').toLowerCase();
    const isAdmin = role === 'sadmin' || role === 'superadmin';
    if (!isAdmin) {
      try {
        localStorage.removeItem('civilRespond_actAsOrgId');
        localStorage.removeItem('civilRespond_actAsOrgs');
      } catch { /* ignore */ }
    }

    localStorage.setItem('authToken', data.token);
    localStorage.setItem('currentUser', JSON.stringify(data.user));

    if (data.user && data.user.language) {
      try { localStorage.setItem('userLanguage', String(data.user.language)); } catch (_) {}
    }

    let initialActiveStoreId: string | null = null;
    if (data.stores && data.stores.length > 0) {
      localStorage.setItem('userStores', JSON.stringify(data.stores));
      if (data.stores.length === 1) {
        const onlyStoreId = data.stores[0].store_id;
        initialActiveStoreId = onlyStoreId;
        localStorage.setItem('activeStoreId', onlyStoreId);
      } else {
        localStorage.removeItem('activeStoreId');
        initialActiveStoreId = null;
      }
    }

    setUser({ ...data.user, stores: data.stores });
    setActiveStoreId(initialActiveStoreId);

    piggybackClaim();

    // v0.4.9.2.3c18: Android.setAuth() now runs for EVERY login,
    // not just users with a traditional storeId. The previous guard
    // `if (initialActiveStoreId && ...)` skipped the bridge call
    // entirely for B2G agents (data.stores is empty for them), which
    // meant KeepAliveService was never started → no FGS → no
    // persistent notification.
    //
    // For B2G agents (user_type 'civil_respondent') we pass their
    // civil_respondent_id as the bridge "storeId". KeepAliveService
    // treats it as opaque and the B2B WS endpoint will reject the
    // connection (returns no alerts), but the FGS starts and the
    // notification shows — agents still receive BOLOs via the
    // existing HTTP polling on /civil-respondent.
    //
    // The fresh JWT is pushed to the native bridge on EVERY login, even
    // when there is no storeId yet.
    //
    // WebAppInterface.setAuth() was written for exactly this case: when
    // storeId is blank it tries to extract one from the JWT, stores the JWT
    // regardless, and starts KeepAliveService "offline" — the WS connects
    // with an empty store and returns no alerts, but the FGS and its
    // notification still come up. KeepAliveServiceCore:426 only rejects a
    // NULL storeId, so "" passes straight through.
    //
    // Previously the whole call sat behind `if (bridgeStoreId && …)`, so a
    // multi-store superadmin/sadmin never pushed a JWT at login. Shared-
    // Preferences therefore kept the PREVIOUS session's JWT, and
    // MainActivity.startServiceIfAuthStored() — which runs in onCreate,
    // before the PWA is even loaded — booted KeepAliveService from that
    // stale token on every app launch. Picking a store eventually
    // refreshed prefs, but only after the app had already started a
    // service on the wrong JWT.
    //
    // Picking a store still calls setAuth again with the real storeId, and
    // KeepAliveServiceCore:439 detects the change and reconnects the WS.
    let bridgeStoreId: string | null = initialActiveStoreId;
    if (!bridgeStoreId) {
      const userType = String(data?.user?.user_type || '').toLowerCase();
      if (userType === 'civil_respondent') {
        // Agents don't have stores — use their civil_respondent_id
        // so the APK has a non-null id to start the FGS.
        bridgeStoreId = (data?.user?.civil_respondent_id as string | undefined) || null;
        if (bridgeStoreId) {
          console.log('[AuthContext] B2G agent — passing civil_respondent_id as bridge storeId so KeepAliveService can start');
        }
      }
    }
    lastSetAuthStoreId.current = bridgeStoreId;
    const hasAndroidBridge = typeof (window as any).Android?.setAuth === 'function';
    if (hasAndroidBridge) {
      try {
        (window as any).Android.setAuth(JSON.stringify({
          jwt: data.token,
          userId: data.user.id,
          storeId: bridgeStoreId ?? '',
          displayName: data.user.name || data.user.email,
        }));
        if (!bridgeStoreId) {
          console.log('[AuthContext] multi-store login — pushed fresh JWT to the bridge with no storeId; service starts offline and picks up the store when one is selected');
        }
      } catch (e) {
        console.warn('[AuthContext] Android.setAuth failed (continuing login):', e);
      }
    } else if (!bridgeStoreId && String(data?.user?.user_type || '').toLowerCase() === 'civil_respondent') {
      // Desktop browsers have no bridge at all and never did — stay quiet
      // for them. A B2G agent on a device with no bridge is the real problem.
      console.warn('[AuthContext] B2G agent with no Android bridge — FGS notification will not show');
    }
  };

  // Password login (real backend).
  const login = async (email: string, password: string): Promise<{ ok: boolean; error?: string }> => {
    try {
      const timezone =
        (typeof Intl !== 'undefined' &&
          Intl.DateTimeFormat &&
          Intl.DateTimeFormat().resolvedOptions &&
          Intl.DateTimeFormat().resolvedOptions().timeZone) ||
        'UTC';
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...apkClientHeader(),
        },
        body: JSON.stringify({ email, password, timezone }),
      });

      if (!response.ok) {
        let serverError: string | undefined;
        try {
          const body = await response.json();
          serverError = body && (body.error || body.code);
        } catch {
          serverError = undefined;
        }
        if (response.status === 403 && serverError === 'mobile_app_required') {
          return { ok: false, error: 'mobile_app_required' };
        }
        if (response.status === 403 && serverError === 'session_exists_mobile') {
          return { ok: false, error: 'session_exists_mobile' };
        }
        if (response.status === 403 && serverError === 'session_exists_desktop') {
          return { ok: false, error: 'session_exists_desktop' };
        }
        if (response.status === 401) {
          return { ok: false, error: 'invalid_credentials' };
        }
        return { ok: false, error: serverError || 'invalid_credentials' };
      }

      const data = await response.json();
      applyLoginResponse(data);
      return { ok: true };
    } catch (error) {
      console.error('Login error:', error);
      return { ok: false, error: 'invalid_credentials' };
    }
  };

  // Passkey login (real backend, discoverable credential flow).
  const loginWithPasskey = async (): Promise<{ ok: boolean; error?: string }> => {
    try {
      const optsRes = await fetch(`${API_BASE}/api/auth/passkey/login/options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!optsRes.ok) {
        console.error('login/options failed:', optsRes.status);
        return { ok: false, error: 'passkey_options_failed' };
      }
      const { options } = await optsRes.json();
      const assertion = await getCredential(options);
      console.log('[passkey] assertion received:', JSON.stringify(assertion, null, 2));

      const timezone =
        (typeof Intl !== 'undefined' &&
          Intl.DateTimeFormat &&
          Intl.DateTimeFormat().resolvedOptions &&
          Intl.DateTimeFormat().resolvedOptions().timeZone) ||
        'UTC';
      const verifyRes = await fetch(`${API_BASE}/api/auth/passkey/login/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...apkClientHeader(),
        },
        body: JSON.stringify({ assertionResponse: assertion, timezone }),
      });
      if (!verifyRes.ok) {
        let serverError: string | undefined;
        try {
          const body = await verifyRes.json();
          serverError = body && (body.error || body.code);
        } catch {
          serverError = undefined;
        }
        if (verifyRes.status === 403 && serverError === 'mobile_app_required') {
          return { ok: false, error: 'mobile_app_required' };
        }
        if (verifyRes.status === 403 && serverError === 'session_exists_mobile') {
          return { ok: false, error: 'session_exists_mobile' };
        }
        if (verifyRes.status === 403 && serverError === 'session_exists_desktop') {
          return { ok: false, error: 'session_exists_desktop' };
        }
        console.error('login/verify failed:', verifyRes.status, serverError);
        return { ok: false, error: serverError || 'passkey_failed' };
      }
      const data = await verifyRes.json();
      applyLoginResponse(data);
      return { ok: true };
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        return { ok: false, error: 'passkey_cancelled' };
      }
      console.error('Passkey login error:', err);
      return { ok: false, error: 'passkey_failed' };
    }
  };

  const registerPasskey = async (nickname?: string): Promise<{ ok: boolean; error?: string }> => {
    try {
      const optsRes = await fetch(`${API_BASE}/api/auth/passkey/register/options`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader(),
        },
      });
      if (!optsRes.ok) {
        const errBody = await optsRes.json().catch(() => ({}));
        return { ok: false, error: errBody.error || `Failed to get registration options (HTTP ${optsRes.status})` };
      }
      const { options } = await optsRes.json();
      const attestation = await createCredential(options);

      const verifyRes = await fetch(`${API_BASE}/api/auth/passkey/register/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader(),
        },
        body: JSON.stringify({
          attestationResponse: attestation,
          nickname: nickname || null,
        }),
      });
      if (!verifyRes.ok) {
        const errBody = await verifyRes.json().catch(() => ({}));
        return { ok: false, error: errBody.error || `Failed to verify registration (HTTP ${verifyRes.status})` };
      }
      return { ok: true };
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        return { ok: false, error: 'cancelled' };
      }
      console.error('registerPasskey error:', err);
      return { ok: false, error: err?.message || 'Unknown error' };
    }
  };

  const listPasskeys = async (): Promise<PasskeyInfo[]> => {
    const res = await fetch(`${API_BASE}/api/auth/passkey/list`, {
      headers: { 'Authorization': getAuthHeader() },
    });
    if (!res.ok) {
      throw new Error(`Failed to list passkeys (HTTP ${res.status})`);
    }
    const { credentials } = await res.json();
    return credentials || [];
  };

  const revokePasskey = async (credentialId: string): Promise<boolean> => {
    const res = await fetch(`${API_BASE}/api/auth/passkey/revoke`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': getAuthHeader(),
      },
      body: JSON.stringify({ credential_id: credentialId }),
    });
    return res.ok;
  };

  // =====================================================================
  // SESSION-KICK HANDLER — HARD REDIRECT
  //
  // The old modal used Tailwind arbitrary values (z-[9999], bg-black/50,
  // fixed inset-0) that don't render reliably in Android System WebView.
  // Instead, clear state and hard-redirect to /login.
  // =====================================================================
  const [kickedReason, setKickedReason] = useState<string | null>(null);
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail || {};
      const reason = detail.reason || 'logged_in_elsewhere';
      console.log('[AuthContext] session-kicked:', reason, '— HARD REDIRECT to /login');
      setKickedReason(reason);
      setUser(null);
      clearAndroidBridge();
      clearAllClientSessionState('session_kicked:' + reason);
      window.location.href = `/login?reason=${encodeURIComponent('session_kicked')}&kickReason=${encodeURIComponent(reason)}`;
    };
    window.addEventListener('iSentinel360:session-kicked', handler);
    return () => window.removeEventListener('iSentinel360:session-kicked', handler);
  }, []);

  const dismissKickedModal = () => {
    setKickedReason(null);
    setTimeout(() => {
      window.location.href = '/login?reason=session-terminated';
    }, 100);
  };

  const logout = async (): Promise<void> => {
    setKickedReason(null);

    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch (e) {
        console.warn('[AuthContext] logout API call failed (continuing):', e);
      }
    }
    setUser(null);
    // v0.4.9.2.3c17: reset the ref so the next login re-arms cleanly.
    lastSetAuthStoreId.current = undefined;
    clearAndroidBridge();
    clearAllClientSessionState('user_logout');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithPasskey,
        registerPasskey,
        listPasskeys,
        revokePasskey,
        logout,
        activeStoreId,
        setActiveStoreId: updateActiveStoreId,
        kickedReason,
        dismissKickedModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};